import { useCallback, useRef, useState } from 'react';

const MAX_FILE_SIZE = 800 * 1024 * 1024; // 800MB

interface UseDragDropOptions {
    onDrop: (files: File[]) => void;
    accept?: string;
    disabled?: boolean;
}

interface UseDragDropReturn {
    isDragging: boolean;
    dragHandlers: {
        onDragEnter: (e: React.DragEvent) => void;
        onDragLeave: (e: React.DragEvent) => void;
        onDragOver: (e: React.DragEvent) => void;
        onDrop: (e: React.DragEvent) => void;
    };
}

const isFileAccepted = (file: File, accept: string): boolean => {
    const acceptedTypes = accept.split(',').map((t) => t.trim().toLowerCase());
    const fileMimeType = file.type.toLowerCase();

    return acceptedTypes.some((type) => {
        if (type.endsWith('/*')) {
            const category = type.slice(0, type.indexOf('/'));
            return fileMimeType.startsWith(category + '/');
        }
        return fileMimeType === type;
    });
};

export function useDragDrop({
    onDrop,
    accept = 'image/*',
    disabled = false
}: UseDragDropOptions): UseDragDropReturn {
    const [isDragging, setIsDragging] = useState(false);
    const dragCounterRef = useRef(0);

    const handleDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current += 1;
        if (e.dataTransfer.types.includes('Files')) {
            setIsDragging(true);
        }
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current -= 1;
        if (dragCounterRef.current === 0) {
            setIsDragging(false);
        }
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            dragCounterRef.current = 0;
            setIsDragging(false);

            if (disabled) return;

            const validFiles = Array.from(e.dataTransfer.files).filter(
                (file) => isFileAccepted(file, accept) && file.size <= MAX_FILE_SIZE
            );

            if (validFiles.length > 0) {
                onDrop(validFiles);
            }
        },
        [disabled, accept, onDrop]
    );

    return {
        isDragging,
        dragHandlers: {
            onDragEnter: handleDragEnter,
            onDragLeave: handleDragLeave,
            onDragOver: handleDragOver,
            onDrop: handleDrop
        }
    };
}
